import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff, LogIn } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_neighbor-market-6/artifacts/78ujw3e9_LocaLS_Logo.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Doodle SVG Background Component
const DoodleBackground = () => (
  <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="doodle-pattern" x="0" y="0" width="280" height="280" patternUnits="userSpaceOnUse">
        {/* 1. Cupcake */}
        <g transform="translate(12, 18) rotate(-8)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12c0-1 1-1.5 3-1.5s3 .5 3 1.5" />
          <path d="M4.5 12v3c0 .5 .8 1 2.5 1s2.5-.5 2.5-1v-3" />
          <path d="M7 10c-2 0-2.5-1-2.5-2.5 0-.8.8-1.5 2.5-1.5s2.5.7 2.5 1.5c0 1.5-.5 2.5-2.5 2.5z" />
        </g>
        
        {/* 2. Ladoo */}
        <g transform="translate(65, 45) rotate(10)" fill="none" stroke="#D95D39" strokeWidth="1.2">
          <circle cx="6" cy="6" r="5" />
          <circle cx="4" cy="5" r=".6" fill="#D95D39" />
          <circle cx="7" cy="4" r=".6" fill="#D95D39" />
          <circle cx="6" cy="8" r=".6" fill="#D95D39" />
        </g>
        
        {/* 3. Plant pot */}
        <g transform="translate(130, 15) rotate(-5)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round">
          <path d="M6 16v-5" />
          <path d="M6 11c-2.5 0-4-1.5-4-4 0 0 1.5 1.5 4 1.5s4-1.5 4-1.5c0 2.5-1.5 4-4 4z" />
          <path d="M4 16h4" />
        </g>
        
        {/* 4. Diya */}
        <g transform="translate(195, 55) rotate(12)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round">
          <ellipse cx="8" cy="12" rx="6" ry="2.5" />
          <path d="M2 12c0-2.5 2.5-5 6-5s6 2.5 6 5" />
          <path d="M8 7v-2" />
          <ellipse cx="8" cy="4" rx="1" ry="1.5" fill="#D95D39" />
        </g>
        
        {/* 5. Book */}
        <g transform="translate(250, 20) rotate(6)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 3h10v12H2z" />
          <path d="M2 3c1-1 3.5-1 5 0 1.5-1 4-1 5 0" />
          <path d="M7 3v12" />
        </g>
        
        {/* 6. Samosa */}
        <g transform="translate(35, 85) rotate(-10)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinejoin="round">
          <path d="M6 3L2 14h8L6 3z" />
          <path d="M4 9h4" />
        </g>
        
        {/* 7. Flower */}
        <g transform="translate(100, 75) rotate(8)" fill="none" stroke="#D95D39" strokeWidth="1.2">
          <circle cx="6" cy="8" r="2" />
          <ellipse cx="6" cy="3" rx="1.2" ry="2.5" />
          <ellipse cx="6" cy="13" rx="1.2" ry="2.5" />
          <ellipse cx="1" cy="8" rx="2.5" ry="1.2" />
          <ellipse cx="11" cy="8" rx="2.5" ry="1.2" />
        </g>
        
        {/* 8. Pencil */}
        <g transform="translate(160, 95) rotate(15)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 13L2 15l2 .5-.5-2z" fill="#D95D39" />
          <path d="M3 13L11 5" />
          <path d="M11 5l1-1-2-2-1 1" />
        </g>
        
        {/* 9. Leaf */}
        <g transform="translate(225, 85) rotate(-7)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round">
          <path d="M6 15c0-6 5-10 5-10s-3 .8-5 5c-2-4.2-5-5-5-5s5 4 5 10z" />
          <path d="M6 8v7" />
        </g>
        
        {/* 10. Tulsi */}
        <g transform="translate(15, 140) rotate(5)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round">
          <path d="M7 16v-7" />
          <path d="M7 9c-1.2-.8-2.5-2.5-2.5-3.5.6.6 1.5 1.2 2.5 1.2" />
          <path d="M7 9c1.2-.8 2.5-2.5 2.5-3.5-.6.6-1.5 1.2-2.5 1.2" />
          <ellipse cx="7" cy="17" rx="4" ry="1.2" />
        </g>
        
        {/* 11. Mithai box */}
        <g transform="translate(70, 130) rotate(-6)" fill="none" stroke="#D95D39" strokeWidth="1.2">
          <rect x="1" y="4" width="14" height="9" rx="1" />
          <path d="M1 7h14" />
          <circle cx="4" cy="10" r="1.2" />
          <circle cx="8" cy="10" r="1.2" />
          <circle cx="12" cy="10" r="1.2" />
        </g>
        
        {/* 12. Rangoli */}
        <g transform="translate(135, 140) rotate(10)" fill="none" stroke="#D95D39" strokeWidth="1.2">
          <circle cx="7" cy="7" r="2.5" />
          <circle cx="7" cy="7" r="5" />
          <path d="M7 0v3m0 8v3M0 7h3m8 0h3" strokeLinecap="round" />
        </g>
        
        {/* 13. Cupcake 2 */}
        <g transform="translate(200, 135) rotate(-12)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12c0-1 1-1.5 3-1.5s3 .5 3 1.5" />
          <path d="M4.5 12v3c0 .5 .8 1 2.5 1s2.5-.5 2.5-1v-3" />
          <path d="M7 10c-2 0-2.5-1-2.5-2.5 0-.8.8-1.5 2.5-1.5s2.5.7 2.5 1.5c0 1.5-.5 2.5-2.5 2.5z" />
        </g>
        
        {/* 14. Ladoo 2 */}
        <g transform="translate(255, 150) rotate(7)" fill="none" stroke="#D95D39" strokeWidth="1.2">
          <circle cx="6" cy="6" r="5" />
          <circle cx="4" cy="5" r=".6" fill="#D95D39" />
          <circle cx="7" cy="4" r=".6" fill="#D95D39" />
          <circle cx="6" cy="8" r=".6" fill="#D95D39" />
        </g>
        
        {/* 15. Diya 2 */}
        <g transform="translate(40, 195) rotate(-9)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round">
          <ellipse cx="8" cy="12" rx="6" ry="2.5" />
          <path d="M2 12c0-2.5 2.5-5 6-5s6 2.5 6 5" />
          <path d="M8 7v-2" />
          <ellipse cx="8" cy="4" rx="1" ry="1.5" fill="#D95D39" />
        </g>
        
        {/* 16. Plant 2 */}
        <g transform="translate(105, 200) rotate(11)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round">
          <path d="M6 16v-5" />
          <path d="M6 11c-2.5 0-4-1.5-4-4 0 0 1.5 1.5 4 1.5s4-1.5 4-1.5c0 2.5-1.5 4-4 4z" />
          <path d="M4 16h4" />
        </g>
        
        {/* 17. Book 2 */}
        <g transform="translate(165, 210) rotate(-8)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 3h10v12H2z" />
          <path d="M2 3c1-1 3.5-1 5 0 1.5-1 4-1 5 0" />
          <path d="M7 3v12" />
        </g>
        
        {/* 18. Samosa 2 */}
        <g transform="translate(230, 195) rotate(6)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinejoin="round">
          <path d="M6 3L2 14h8L6 3z" />
          <path d="M4 9h4" />
        </g>
        
        {/* 19. Flower 2 */}
        <g transform="translate(20, 250) rotate(9)" fill="none" stroke="#D95D39" strokeWidth="1.2">
          <circle cx="6" cy="8" r="2" />
          <ellipse cx="6" cy="3" rx="1.2" ry="2.5" />
          <ellipse cx="6" cy="13" rx="1.2" ry="2.5" />
          <ellipse cx="1" cy="8" rx="2.5" ry="1.2" />
          <ellipse cx="11" cy="8" rx="2.5" ry="1.2" />
        </g>
        
        {/* 20. Leaf 2 */}
        <g transform="translate(85, 255) rotate(-5)" fill="none" stroke="#D95D39" strokeWidth="1.2" strokeLinecap="round">
          <path d="M6 15c0-6 5-10 5-10s-3 .8-5 5c-2-4.2-5-5-5-5s5 4 5 10z" />
          <path d="M6 8v7" />
        </g>
        
        {/* Scattered dots */}
        <circle cx="50" cy="35" r="1.2" fill="#D95D39" />
        <circle cx="180" cy="40" r="1" fill="#D95D39" />
        <circle cx="115" cy="55" r="1.2" fill="#D95D39" />
        <circle cx="240" cy="110" r="1" fill="#D95D39" />
        <circle cx="55" cy="115" r="1.2" fill="#D95D39" />
        <circle cx="175" cy="175" r="1" fill="#D95D39" />
        <circle cx="260" cy="220" r="1.2" fill="#D95D39" />
        <circle cx="145" cy="245" r="1" fill="#D95D39" />
        <circle cx="210" cy="260" r="1.2" fill="#D95D39" />
        <circle cx="265" cy="70" r="1" fill="#D95D39" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#doodle-pattern)" />
  </svg>
);

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        setError('Access denied. Admin privileges required.');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-primary flex items-center justify-center px-4 relative overflow-hidden">
      {/* Doodle Background */}
      <DoodleBackground />
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-2xl p-4 shadow-lg border border-surface-border">
            <img 
              src={LOGO_URL} 
              alt="LocaLS Logo" 
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-surface-border">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-bold text-text-primary mb-2">
              Admin Login
            </h1>
            <p className="font-body text-text-secondary">
              Sign in to access the admin panel
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-body">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body font-medium text-text-primary">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="h-12 rounded-xl border-surface-border focus:border-brand-primary focus:ring-brand-primary"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-body font-medium text-text-primary">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12 rounded-xl border-surface-border focus:border-brand-primary focus:ring-brand-primary pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl font-body font-semibold"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <LogIn size={20} />
                  Sign In
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Back to home link */}
        <div className="text-center mt-6">
          <a 
            href="/" 
            className="font-body text-text-secondary hover:text-brand-primary transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
